import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { HeartHandshake, Loader2, Sparkles, Heart } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { UserAvatar, UserLink } from "@/components/UserBits";
import { GoldCheck } from "@/components/GoldCheck";
import { SupporterGoal } from "@/components/FeedExtras";
import { usePageMeta } from "@/lib/seo";
import { GreetingEditor, GreetingBubble } from "@/components/SupporterGreeting";
import { FloatingHearts } from "@/components/profile/SupporterHeartArt";
import { Glitter } from "@/components/SupporterGlitter";

const since = (d) => (d ? new Date(d).toLocaleDateString("de-DE", { month: "long", year: "numeric" }) : null);

const Spotlight = ({ u }) => (
  <div className="sp-post sp-glow rounded-[2rem] p-6 sm:p-8 flex flex-col sm:flex-row items-center gap-5 text-center sm:text-left fade-up" data-testid="supporters-top">
    <Glitter />
    <div className="relative shrink-0">
      <UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-28 h-28" text="text-4xl" crown={false} noStory />
      <Heart className="absolute -top-6 -left-3 -rotate-[24deg] w-12 h-12 text-rose-500 stroke-white drop-shadow-md" fill="currentColor" />
    </div>
    <div className="min-w-0 space-y-1.5">
      <span className="inline-block text-[10px] leading-5 px-2.5 rounded-full font-extrabold tracking-[0.2em] text-white bg-gradient-to-r from-orange-400 to-pink-500">TOP UNTERSTÜTZER</span>
      <p className="font-display text-2xl sm:text-3xl font-extrabold flex items-center justify-center sm:justify-start gap-2"><UserLink id={u.user_id}>{u.name}</UserLink><GoldCheck className="w-6 h-6" /></p>
      {since(u.supporter_since) && <p className="text-sm text-slate-500">Unterstützt uns seit {since(u.supporter_since)}</p>}
      <GreetingBubble text={u.supporter_greeting} testId={`supporter-greeting-${u.user_id}`} />
    </div>
  </div>
);

const Tile = ({ u, rank, i }) => (
  <li className="sp-post sp-glow rounded-3xl p-4 flex items-center gap-3 fade-up" style={{ animationDelay: `${Math.min(i, 20) * 40}ms` }} data-testid={`supporters-item-${u.user_id}`}>
    <Glitter size="sm" />
    <span className="w-7 text-center font-display font-extrabold text-amber-700/70 tabular-nums">{rank}</span>
    <UserAvatar userId={u.user_id} name={u.name} picture={u.picture} size="w-12 h-12" text="text-base" />
    <div className="min-w-0 flex-1">
      <p className="font-bold flex items-center gap-1.5 min-w-0"><UserLink id={u.user_id} className="truncate">{u.name}</UserLink><GoldCheck className="w-4 h-4 shrink-0" /></p>
      {since(u.supporter_since) && <p className="text-xs text-slate-500">seit {since(u.supporter_since)}</p>}
      <GreetingBubble text={u.supporter_greeting} testId={`supporter-greeting-${u.user_id}`} />
    </div>
  </li>
);

export default function Supporters() {
  usePageMeta({ title: "Unsere Projekt Unterstützer" });
  const { user } = useAuth();
  const [list, setList] = useState(null);
  useEffect(() => { api.get("/supporters").then(({ data }) => setList(data)).catch(() => setList([])); }, []);
  const [top, ...rest] = list || [];
  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="supporters-page">
      <header className="premium-hero relative rounded-[2rem] px-6 py-9 sm:py-12 text-center overflow-hidden text-white" data-testid="supporters-hero">
        <FloatingHearts />
        <div className="relative">
          <span className="mx-auto w-16 h-16 rounded-full bg-white text-rose-500 ring-4 ring-amber-200/70 flex items-center justify-center shadow-xl"><HeartHandshake className="w-8 h-8" /></span>
          <p className="mt-4 text-[11px] font-extrabold tracking-[0.35em] text-amber-100">DANKE VON HERZEN</p>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold text-white mt-1" data-testid="supporters-title">Unsere Projekt Unterstützer</h1>
          <p className="text-sm sm:text-base text-white/90 mt-3 max-w-lg mx-auto">Diese Menschen tragen die Kosten unserer Community mit und machen itzPlay möglich. Ihr seid großartig! ❤️</p>
          {list && <p className="mt-4 inline-flex items-center gap-1.5 text-sm font-bold text-amber-100" data-testid="supporters-count"><Sparkles className="w-4 h-4" /> {list.length} Unterstützer</p>}
        </div>
      </header>
      <SupporterGoal prefix="page-" />
      {user?.supporter && <GreetingEditor onSaved={(g) => setList((l) => l?.map((x) => (x.user_id === user.user_id ? { ...x, supporter_greeting: g } : x)))} />}
      {list === null ? <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-amber-500" /></div>
        : !top ? <p className="text-center text-slate-500 py-10" data-testid="supporters-empty">Noch keine Unterstützer – sei die/der Erste! 💖</p>
          : <>
            <Spotlight u={top} />
            {rest.length > 0 && <ul className="grid sm:grid-cols-2 gap-3" data-testid="supporters-list">{rest.map((u, i) => <Tile key={u.user_id} u={u} rank={i + 2} i={i} />)}</ul>}
          </>}
      {!user?.supporter && (
        <Link to="/community-unterstuetzen" data-testid="supporters-cta" className="h-14 rounded-full pink-gradient text-white font-extrabold flex items-center justify-center gap-2 shadow-lg shadow-rose-300/60 active:scale-[0.98] transition-transform">
          <HeartHandshake className="w-5 h-5" /> Auch Projekt Unterstützer werden
        </Link>
      )}
    </div>
  );
}
